import React, { createContext, useContext, useCallback, useEffect, ReactNode } from 'react';
import { useDomainFetch } from './hooks/useDomainFetch';
import { useAuth } from './AuthContext';
import { useSettings } from './SettingsContext';
import logger from '../utils/logger';
import { Anomaly } from '../types';
import { API_BASE_URL } from '../config/api';

interface AnomaliesContextType {
  // Data state
  anomalies: Anomaly[] | null;
  loading: boolean;
  error: string | null;
  isStale: boolean;
  lastFetch: number | null;

  // Actions
  refetch: () => Promise<void>;

  // Domain-specific actions (unchanged external API)
  acknowledgeAnomaly: (anomalyId: string) => Promise<boolean>;
  getAnomaliesByDevice: (deviceId: string) => Anomaly[];
  getAnomaliesBySeverity: (severity: Anomaly['severity']) => Anomaly[];
  getUnacknowledgedCount: () => number;
  getCriticalAnomalies: () => Anomaly[];
}

export const AnomaliesContext = createContext<AnomaliesContextType | undefined>(undefined);
export type { AnomaliesContextType };

interface AnomaliesProviderProps {
  children: ReactNode;
}

export function AnomaliesProvider({ children }: AnomaliesProviderProps) {
  const { refreshToken, logout } = useAuth();
  const { settings } = useSettings();

  const getToken = useCallback(
    () => (typeof window !== 'undefined' ? localStorage.getItem('tb_token') : null),
    [],
  );

  const onUnauthorized = useCallback(
    async (makeRequest: () => Promise<Response>): Promise<Response> => {
      const refreshTokenValue =
        typeof window !== 'undefined' ? localStorage.getItem('tb_refresh_token') : null;
      if (refreshTokenValue) {
        const refreshSuccess = await refreshToken();
        if (refreshSuccess) {
          return makeRequest();
        } else {
          logout();
        }
      } else {
        logout();
      }
      return new Response(null, { status: 401 });
    },
    [refreshToken, logout],
  );

  const {
    state: anomaliesState,
    refetch: refetchAnomalies,
  } = useDomainFetch<Anomaly[]>({
    endpoint: `${API_BASE_URL}/api/anomalies`,
    refreshInterval: settings.refreshIntervals.anomalies,
    getToken,
    onUnauthorized,
    onSuccess: (raw) => {
      // The API wraps anomaly responses in { success, data } via sendSuccess()
      // The raw value is already stored -- we extract in the context value below.
      void raw;
    },
  });

  // Initial fetch on mount
  useEffect(() => {
    if (anomaliesState.isStale || !anomaliesState.lastFetch) {
      refetchAnomalies();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Re-fetch when marked stale by interval
  useEffect(() => {
    if (anomaliesState.isStale && anomaliesState.lastFetch) {
      refetchAnomalies();
    }
  }, [anomaliesState.isStale, anomaliesState.lastFetch, refetchAnomalies]);

  const refetch = useCallback(async (): Promise<void> => {
    await refetchAnomalies(true);
  }, [refetchAnomalies]);

  // The anomalies API wraps in { success, data } -- extract the actual array
  const anomaliesData: Anomaly[] | null = (() => {
    if (!anomaliesState.data) return null;
    if (Array.isArray(anomaliesState.data)) return anomaliesState.data;
    const wrapped = anomaliesState.data as unknown as { data?: Anomaly[] };
    return wrapped.data ?? null;
  })();

  const acknowledgeAnomaly = useCallback(async (anomalyId: string): Promise<boolean> => {
    const startTime = Date.now();
    try {
      logger.info(`Acknowledging anomaly ${anomalyId}`, 'AnomaliesContext');
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/anomalies/${anomalyId}/acknowledge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      const duration = Date.now() - startTime;
      logger.logNetworkActivity('POST', `/api/anomalies/${anomalyId}/acknowledge`, response.status, duration);
      if (response.ok) {
        await refetch();
        logger.info(`Successfully acknowledged anomaly ${anomalyId}`, 'AnomaliesContext');
        return true;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error(`Failed to acknowledge anomaly ${anomalyId}: ${errorMessage}`, 'AnomaliesContext');
      return false;
    }
  }, [getToken, refetch]);

  const getAnomaliesByDevice = useCallback((deviceId: string): Anomaly[] => {
    if (!anomaliesData) return [];
    return anomaliesData.filter((a) => a.deviceId === deviceId);
  }, [anomaliesData]);

  const getAnomaliesBySeverity = useCallback((severity: Anomaly['severity']): Anomaly[] => {
    if (!anomaliesData) return [];
    return anomaliesData.filter((a) => a.severity === severity);
  }, [anomaliesData]);

  const getUnacknowledgedCount = useCallback((): number => {
    if (!anomaliesData) return 0;
    return anomaliesData.filter((a) => !a.acknowledged).length;
  }, [anomaliesData]);

  const getCriticalAnomalies = useCallback((): Anomaly[] => {
    if (!anomaliesData) return [];
    return anomaliesData.filter((a) => a.severity === 'critical' && !a.acknowledged);
  }, [anomaliesData]);

  const contextValue: AnomaliesContextType = {
    anomalies: anomaliesData,
    loading: anomaliesState.loading,
    error: anomaliesState.error,
    isStale: anomaliesState.isStale,
    lastFetch: anomaliesState.lastFetch,
    refetch,
    acknowledgeAnomaly,
    getAnomaliesByDevice,
    getAnomaliesBySeverity,
    getUnacknowledgedCount,
    getCriticalAnomalies,
  };

  return (
    <AnomaliesContext.Provider value={contextValue}>
      {children}
    </AnomaliesContext.Provider>
  );
}

export function useAnomalies(): AnomaliesContextType {
  const context = useContext(AnomaliesContext);
  if (context === undefined) {
    throw new Error('useAnomalies must be used within an AnomaliesProvider');
  }
  return context;
}

export default AnomaliesProvider;
